import { useEffect, useState } from "react";
import {
  SHOWS, EVENT_TITLE, EVENT_FORMAT, FEATURING, PRESENTER, POSTER_ALT,
} from "../data/shows";
import posters from "../data/posters.json";
import { SITE_URL } from "./constants";

/**
 * Show helpers. Everything here derives from src/data/shows.js; nothing about
 * a show is spelled out in this file.
 *
 * Expiry runs on the Asia/Kolkata calendar date: a show is upcoming up to and
 * including its own date, and gone the day after.
 */

// Today's date in India as YYYY-MM-DD.
export function todayIST(now = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(now);
}

// "Upcoming" means ON SALE: still ahead and not sold out. Every Book button,
// the sticky bar and the nav's "Book tickets" key off this list, so a sold-out
// show drops out of all of them at once.
export function upcomingShows(today = todayIST()) {
  return SHOWS.filter((s) => s.date >= today && !s.soldOut && s.bookingUrl);
}

// Sold-out shows stay on the home page as proof, whatever their date.
export function soldOutShows() {
  return SHOWS.filter((s) => s.soldOut);
}

// The build day, baked into the bundle by craco.config.js. The prerendered HTML
// was rendered on this day, so the first client render uses it too and the
// hydration matches; the real date takes over straight after mount. That is
// what lets a stale deploy hide a show once its date has passed.
const BUILD_DAY = process.env.REACT_APP_BUILD_DAY || todayIST();

// Today in India, starting from the build day for a matching hydration.
export function useTodayIST() {
  const [today, setToday] = useState(BUILD_DAY);
  useEffect(() => setToday(todayIST()), []);
  return today;
}

export function useUpcomingShows() {
  return upcomingShows(useTodayIST());
}

// ── Labels ──────────────────────────────────────────────────────────────
const asDate = (iso) => new Date(`${iso}T12:00:00+05:30`);
const fmt = (iso, opts) =>
  new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", ...opts }).format(asDate(iso));

/** "Friday, 23 October 2026" */
export function fullDate(show) {
  const weekday = fmt(show.date, { weekday: "long" });
  return `${weekday}, ${fmt(show.date, { day: "numeric", month: "long", year: "numeric" })}`;
}

/** "Fri 23 Oct" */
export function shortDate(show) {
  return `${fmt(show.date, { weekday: "short" })} ${fmt(show.date, { day: "numeric", month: "short" })}`;
}

/** "18:30" -> "6:30 PM" */
export function clock(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
}

/** ["Doors 6:30 PM", "Show 7:00 PM"], skipping whatever is null. */
export function timeLines(show) {
  return [
    show.doorsTime && `Doors ${clock(show.doorsTime)}`,
    show.startTime && `Show ${clock(show.startTime)}`,
  ].filter(Boolean);
}

/** "₹499", or null when the price is not known. */
export function priceLabel(show) {
  if (show.priceFrom == null) return null;
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency: "INR", maximumFractionDigits: 0,
  }).format(show.priceFrom);
}

/** "Tickets from ₹499", or null. */
export function fromLabel(show) {
  const p = priceLabel(show);
  return p && `Tickets from ${p}`;
}

/** Whole days from `today` to the show, both YYYY-MM-DD. */
export function daysUntil(show, today = todayIST()) {
  const ms = Date.parse(`${show.date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
  return Math.round(ms / 86400000);
}

/** "Today", "Tomorrow", "9 days to go". */
export function countdownLabel(show, today = todayIST()) {
  const d = daysUntil(show, today);
  if (d <= 0) return "Today";
  if (d === 1) return "Tomorrow";
  return `${d} days to go`;
}

/** "23 Oct · 9 days left" (or "Today", "Tomorrow"): the short form for the
 *  mobile action bar, where a half-width button has room for little. */
export function compactCountdown(show, today = todayIST()) {
  const d = daysUntil(show, today);
  const when = d <= 0 ? "Today" : d === 1 ? "Tomorrow" : `${d} days left`;
  return `${fmt(show.date, { day: "numeric", month: "short" })} · ${when}`;
}

/** "Book Delhi · Fri 23 Oct" */
export function bookLabel(show) {
  return `Book ${show.city} · ${shortDate(show)}`;
}

// ── Posters ─────────────────────────────────────────────────────────────
// Derivatives are written by scripts/build-poster-images.mjs into
// public/posters/, with their sizes and LQIP in src/data/posters.json.
const P = process.env.PUBLIC_URL || "";
export { POSTER_ALT };

export function poster(name) {
  const p = posters.posters[name];
  const url = (w, ext) => `${P}/posters/${name}-${w}.${ext}`;
  const set = (ext) => p.widths.map((w) => `${url(w, ext)} ${w}w`).join(", ");
  const largest = p.widths[p.widths.length - 1];
  return {
    ...p,
    avif: set("avif"),
    webp: set("webp"),
    jpg: set("jpg"),
    src: url(largest, "jpg"),
  };
}

// Absolute JPEG URLs, for og:image and the JSON-LD.
export const OG_SHOW_IMAGE = {
  url: `${SITE_URL}/posters/bhaav-sangam-landscape-1200.jpg`,
  width: 1200,
  height: 600,
};
const absJpg = (name) => {
  const p = posters.posters[name];
  return `${SITE_URL}/posters/${name}-${p.widths[p.widths.length - 1]}.jpg`;
};

// ── JSON-LD ─────────────────────────────────────────────────────────────
export function showsJsonLd(shows) {
  return shows.map((s) => ({
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    name: `${EVENT_TITLE}: ${EVENT_FORMAT} featuring ${FEATURING} (${s.city})`,
    startDate: s.startTime ? `${s.date}T${s.startTime}:00+05:30` : s.date,
    ...(s.doorsTime ? { doorTime: `${s.date}T${s.doorsTime}:00+05:30` } : {}),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    location: {
      "@type": "Place",
      name: s.address.name,
      address: {
        "@type": "PostalAddress",
        streetAddress: s.address.streetAddress,
        addressLocality: s.address.addressLocality,
        postalCode: s.address.postalCode,
        addressCountry: s.address.addressCountry,
      },
    },
    image: [absJpg("bhaav-sangam-landscape"), absJpg("bhaav-sangam-portrait")],
    performer: [
      { "@type": "MusicGroup", name: "Antara" },
      { "@type": "Person", name: FEATURING },
    ],
    organizer: { "@type": "Organization", name: PRESENTER },
    offers: s.soldOut
      ? { "@type": "Offer", availability: "https://schema.org/SoldOut", priceCurrency: "INR" }
      : {
          "@type": "Offer",
          url: s.bookingUrl,
          availability: "https://schema.org/InStock",
          ...(s.priceFrom != null ? { price: s.priceFrom } : {}),
          priceCurrency: "INR",
        },
  }));
}
